import { useMemo } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { useToast } from '@givebutter/primitives/toast';
import { deleteUserInvite, fetchPendingUsers } from 'api/account';
import { CopyButton } from 'components/copy-button';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { accountQueryKeys } from 'queries/account-query-keys';
import { PendingMember } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<PendingMember>();

const PendingUsers = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    const { pagination, setPagination } = Table.useReactTableState();

    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
        },
        500,
    );

    const {
        data: pendingUsers = {
            data: [],
            meta: { last_page: -1, current_page: 1 },
        },
    } = useSuspenseQuery({
        queryKey: accountQueryKeys.pendingUsersWithFilters(accountId, params),
        queryFn: () => fetchPendingUsers(accountId, params),
    });

    const { mutate: deleteInvite, isPending: isDeleting } = useMutation<
        void,
        GivebutterAxiosError,
        { id: string }
    >({
        mutationFn: ({ id }) => deleteUserInvite(accountId, id),
        onSuccess: () => {
            toast.success('Deleted invite successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.pendingUsers(accountId),
                type: 'all',
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to delete invite');
        },
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('email', {
                header: 'Pending Invites',
                cell: info => {
                    const user = info.row.original;
                    return (
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                                <a href={`mailto:${user.email}`} className="text-base">
                                    {user.email}
                                </a>
                                <CopyButton text={user.email} />
                            </div>
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const user = info.row.original;

                    return (
                        <DropdownMenu.Root key={user.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align="end">
                                <AlertDialog.Root type="error">
                                    <AlertDialog.Trigger type="reset" asChild={true}>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            disabled={isDeleting}
                                        >
                                            Delete
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Delete user invite
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    Are you sure?
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => deleteInvite({ id: user.id })}
                                                >
                                                    Delete
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [deleteInvite, isDeleting],
    );

    const table = useReactTable({
        data: pendingUsers?.data ?? [],
        columns,
        pageCount: pendingUsers?.meta?.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, pendingUsers);

    return (
        <Box className="overflow-hidden">
            <Table.Root isLoading={isPending} variant="bordered" table={table}>
                <Table.Content>
                    <Table.Body />
                </Table.Content>
                <Table.Empty variant={'secondary'}>
                    <Table.EmptyIcon />
                    <Table.EmptyTitle>No pending invitations</Table.EmptyTitle>
                </Table.Empty>
                <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </Table.Root>
        </Box>
    );
};

export { PendingUsers };
